import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleNavigation = (href: string) => {
    if (href.startsWith('/#')) {
      const sectionId = href.substring(2);
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      navigate(href);
      window.scrollTo(0, 0);
    }
    setIsMenuOpen(false);
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDropdownEnter = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setIsDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => setIsDropdownOpen(false), 150);
  };

  const productLinks = [
    { name: "Peveo Maxed", href: "/products/pre-workout-maxed", price: "499 kr", highlight: true },
    { name: "PWO-Stim-free", href: "/products/pre-workout", price: "299 kr" },
    { name: "Shaker", href: "/products/shaker", price: "39 kr" },
    { name: "Liquid Chalk", href: "/products/chalk", price: "48 kr" },
    { name: "Tennissokker (10-pakning)", href: "/products/tennis-socks", price: "249 kr" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 relative">
          <div className="flex items-center absolute left-0">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img
                src="/lovable-uploads/a3c5be73-56da-4b81-b053-3ff5d2ce3825.png"
                alt="PEVEO Logo"
                className="h-10 w-auto"
              />
              <span className="font-orbit text-3xl font-bold text-foreground ml-1">EVEO</span>
            </Link>
          </div>

          {/* Desktop navigation */}
          <div className="hidden md:block flex-1">
            <div className="flex items-baseline space-x-4 justify-center">
              <button
                onClick={() => handleNavigation("/")}
                className="text-foreground hover:text-primary px-3 py-2 text-sm font-medium transition-colors relative group"
              >
                Hjem
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></span>
              </button>

              <div
                className="relative"
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={handleDropdownLeave}
              >
                <button className="flex items-center gap-1 text-foreground hover:text-primary px-3 py-2 text-sm font-medium transition-colors relative group">
                  Produkter
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></span>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-[9999]">
                    <div className="w-64 bg-white border border-border shadow-lg rounded-lg overflow-hidden">
                      {productLinks.map((product) => (
                        <button
                          key={product.name}
                          onClick={() => { handleNavigation(product.href); setIsDropdownOpen(false); }}
                          className={`flex justify-between items-center w-full px-4 py-3 text-left hover:bg-muted transition-colors ${
                            product.highlight ? 'bg-primary/5 border-l-2 border-primary' : ''
                          }`}
                        >
                          <span className={`text-sm font-medium ${product.highlight ? 'text-primary' : 'text-foreground'}`}>
                            {product.name}
                          </span>
                          <span className={`text-sm font-bold ${product.highlight ? 'text-primary' : 'text-muted-foreground'}`}>
                            {product.price}
                          </span>
                        </button>
                      ))}
                      <div className="border-t border-border">
                        <button
                          onClick={() => { handleNavigation("/#products"); setIsDropdownOpen(false); }}
                          className="w-full px-4 py-3 text-center text-sm font-medium text-primary hover:bg-muted transition-colors"
                        >
                          Se alle produkter
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => handleNavigation("/om-oss")}
                className="text-foreground hover:text-primary px-3 py-2 text-sm font-medium transition-colors relative group"
              >
                Om oss
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></span>
              </button>

              <button
                onClick={() => handleNavigation("/kontakt")}
                className="text-foreground hover:text-primary px-3 py-2 text-sm font-medium transition-colors relative group"
              >
                Kontakt
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></span>
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden absolute right-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-foreground hover:text-primary hover:bg-muted"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile navigation menu */}
        {isMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden"
              onClick={() => setIsMenuOpen(false)}
            />
            <div className="fixed inset-x-0 top-16 z-50 md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-border shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto">
                <button
                  onClick={() => handleNavigation("/")}
                  className="text-foreground hover:text-primary hover:bg-muted block px-3 py-3 text-base font-medium transition-colors rounded-md w-full text-left"
                >
                  Hjem
                </button>

                <div className="space-y-1">
                  <div className="text-foreground px-3 py-3 text-base font-medium">Produkter</div>
                  {productLinks.map((product) => (
                    <button
                      key={product.name}
                      onClick={() => handleNavigation(product.href)}
                      className="text-muted-foreground hover:text-primary hover:bg-muted block px-6 py-3 text-sm font-medium transition-colors rounded-md w-full text-left"
                    >
                      {product.name}
                    </button>
                  ))}
                  <button
                    onClick={() => handleNavigation("/#products")}
                    className="text-primary hover:bg-muted block px-6 py-3 text-sm font-medium transition-colors rounded-md w-full text-left"
                  >
                    Se alle produkter
                  </button>
                </div>

                <button
                  onClick={() => handleNavigation("/om-oss")}
                  className="text-foreground hover:text-primary hover:bg-muted block px-3 py-3 text-base font-medium transition-colors rounded-md w-full text-left"
                >
                  Om oss
                </button>

                <button
                  onClick={() => handleNavigation("/kontakt")}
                  className="text-foreground hover:text-primary hover:bg-muted block px-3 py-3 text-base font-medium transition-colors rounded-md w-full text-left"
                >
                  Kontakt
                </button>
              </div>
            </div>
          </>
        )}
      </nav>
    </header>
  );
};

export default Header;
